import type { AxiosError } from 'axios';

/** Failure dimensions returned by 403 AUDIENCE_NOT_ELIGIBLE responses. */
export type AudienceFailureDimension =
  | 'gender_not_in_audience'
  | 'country_not_in_audience'
  | 'state_not_in_audience'
  | 'city_not_in_audience'
  | 'club_name_not_in_audience'
  | 'batting_hand_not_in_audience'
  | 'batting_order_not_in_audience'
  | 'bowling_arm_not_in_audience'
  | 'bowling_style_not_in_audience'
  | 'below_minimum_age'
  | 'above_maximum_age'
  | 'birth_date_missing'
  | 'wicket_keeper_mismatch'
  | 'missing_profile';

/** Friendly-text table from the admin contract §7. */
export const AUDIENCE_FAILURE_LABELS: Record<AudienceFailureDimension, string> = {
  gender_not_in_audience: 'Wrong gender',
  country_not_in_audience: 'Outside target country',
  state_not_in_audience: 'Outside target state',
  city_not_in_audience: 'Outside target city',
  club_name_not_in_audience: 'Not in target club',
  batting_hand_not_in_audience: 'Wrong batting hand',
  batting_order_not_in_audience: 'Wrong batting order',
  bowling_arm_not_in_audience: 'Wrong bowling arm',
  bowling_style_not_in_audience: 'Wrong bowling style',
  below_minimum_age: 'Too young',
  above_maximum_age: 'Too old',
  birth_date_missing: 'Date of birth not set',
  wicket_keeper_mismatch: 'Wicket-keeper restriction not met',
  missing_profile: 'User profile incomplete',
};

export interface EntitlementShortfall {
  required_entitlement_type: string;
  entry_cost_quantity: number;
  available: number;
}

/**
 * Normalised application-facing API error. UI layers should consume this,
 * not the raw AxiosError, so we always show meaningful messages.
 *
 * Two error families get first-class typed fields because the UI renders
 * them specifically:
 *  - `INSUFFICIENT_ENTITLEMENT_BALANCE` (402)
 *  - `AUDIENCE_NOT_ELIGIBLE` (403)
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string[]>;
  readonly requestId?: string;
  readonly raw?: unknown;
  readonly entitlement?: EntitlementShortfall;
  readonly audienceFailures?: AudienceFailureDimension[];

  constructor(args: {
    message: string;
    status: number;
    code: string;
    fieldErrors?: Record<string, string[]>;
    requestId?: string;
    raw?: unknown;
    entitlement?: EntitlementShortfall;
    audienceFailures?: AudienceFailureDimension[];
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.fieldErrors = args.fieldErrors ?? {};
    this.requestId = args.requestId;
    this.raw = args.raw;
    this.entitlement = args.entitlement;
    this.audienceFailures = args.audienceFailures;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isValidation(): boolean {
    return this.status === 400 || this.status === 422;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isPaymentRequired(): boolean {
    return this.status === 402;
  }
  get isInsufficientEntitlement(): boolean {
    return this.code === 'INSUFFICIENT_ENTITLEMENT_BALANCE';
  }
  get isAudienceIneligible(): boolean {
    return this.code === 'AUDIENCE_NOT_ELIGIBLE';
  }
  get isTenantRequired(): boolean {
    return this.status === 400 && this.code === 'tenant_required';
  }
  get isNotTenantAdmin(): boolean {
    return this.status === 403 && this.code === 'not_a_tenant_admin';
  }
  get isServer(): boolean {
    return this.status >= 500;
  }

  /** Pre-formatted human strings for the audience failure list. */
  get audienceFailureLabels(): string[] {
    return (this.audienceFailures ?? []).map((d) => AUDIENCE_FAILURE_LABELS[d] ?? d);
  }
}

interface ApiErrorBody {
  message?: string;
  /** A plain string, or the platform's nested `{ error: { code, message } }` shape. */
  error?: string | { code?: string | number; message?: string };
  code?: string;
  errors?: Record<string, string[]> | Array<{ field?: string; message?: string }>;
  detail?: string;
  request_id?: string;
  // 402 INSUFFICIENT_ENTITLEMENT_BALANCE structured fields
  required_entitlement_type?: string;
  entry_cost_quantity?: number;
  available?: number;
  // 403 AUDIENCE_NOT_ELIGIBLE structured fields
  failed_dimensions?: string[];
}

function extractFieldErrors(body: ApiErrorBody | undefined): Record<string, string[]> {
  // Boom errors from the platform carry a single field as
  // `{ error: { message, data: { field } } }` (How to Play, How it works).
  const nested = (body as { error?: unknown } | undefined)?.error;
  if (!body?.errors && nested && typeof nested === 'object') {
    const { message, data } = nested as { message?: unknown; data?: { field?: unknown } };
    if (data && typeof data.field === 'string' && data.field) {
      return { [data.field]: typeof message === 'string' && message ? [message] : [] };
    }
  }
  if (!body?.errors) return {};
  if (Array.isArray(body.errors)) {
    const out: Record<string, string[]> = {};
    for (const e of body.errors) {
      if (!e.field) continue;
      out[e.field] = out[e.field] ?? [];
      if (e.message) out[e.field].push(e.message);
    }
    return out;
  }
  return body.errors;
}

const DEFAULT_MESSAGES: Record<number, string> = {
  400: 'The request was invalid.',
  401: 'You need to sign in again.',
  402: 'Insufficient balance for this action.',
  403: 'You do not have permission to perform this action.',
  404: 'We could not find what you were looking for.',
  409: 'This action conflicts with the current state.',
  422: 'Please correct the highlighted fields.',
  429: 'Too many requests — please wait a moment.',
  500: 'Something went wrong on our side.',
  502: 'The service is temporarily unavailable.',
  503: 'The service is temporarily unavailable.',
  504: 'The service took too long to respond.',
};

function extractEntitlementShortfall(body: ApiErrorBody | undefined): EntitlementShortfall | undefined {
  if (!body) return undefined;
  if (
    typeof body.required_entitlement_type === 'string' &&
    typeof body.entry_cost_quantity === 'number' &&
    typeof body.available === 'number'
  ) {
    return {
      required_entitlement_type: body.required_entitlement_type,
      entry_cost_quantity: body.entry_cost_quantity,
      available: body.available,
    };
  }
  return undefined;
}

function extractAudienceFailures(body: ApiErrorBody | undefined): AudienceFailureDimension[] | undefined {
  if (!body || !Array.isArray(body.failed_dimensions)) return undefined;
  return body.failed_dimensions.filter((d): d is AudienceFailureDimension =>
    Object.prototype.hasOwnProperty.call(AUDIENCE_FAILURE_LABELS, d),
  );
}

export function normaliseAxiosError(error: AxiosError): ApiError {
  const status = error.response?.status ?? 0;
  const body = error.response?.data as ApiErrorBody | undefined;
  const requestId =
    (error.response?.headers?.['x-request-id'] as string | undefined) ?? body?.request_id;

  if (!error.response) {
    return new ApiError({
      message: error.message || 'Network error — please check your connection.',
      status: 0,
      code: 'network_error',
      raw: error,
    });
  }

  // The platform answers `{ error: { code, message } }`; reading `body.error`
  // as a string surfaced "[object Object]" in the UI.
  const nestedError = body?.error && typeof body.error === 'object' ? body.error : undefined;
  const stringError = typeof body?.error === 'string' ? body.error : undefined;
  const message =
    (typeof body?.message === 'string' ? body.message : undefined) ??
    nestedError?.message ??
    stringError ??
    body?.detail ??
    DEFAULT_MESSAGES[status] ??
    'Request failed.';
  const code =
    body?.code ?? (typeof nestedError?.code === 'string' ? nestedError.code : undefined) ?? `http_${status}`;

  return new ApiError({
    message,
    status,
    code,
    fieldErrors: extractFieldErrors(body),
    requestId,
    raw: body,
    entitlement: extractEntitlementShortfall(body),
    audienceFailures: extractAudienceFailures(body),
  });
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
